import React from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
    alpha,
    useTheme,
} from "@mui/material";
import {
    AccessTime as AccessTimeIcon,
    Close as CloseIcon,
    LocationOn as LocationOnIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    RateReview as RateReviewIcon,
    Send as SendIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
} from "@mui/icons-material";
import ImageLoader from "components/ImageLoader";
import { sanitizePhoneNumber } from "../../../utils/phoneUtils";

const CompanyInfoDialog = ({
    open,
    onClose,
    companyData,
    reviews,
    reviewsLoading,
    ratingForm,
    setRatingForm,
    isRatingPhoneInvalid,
    ratingSubmitting,
    onSubmitReview,
}) => {
    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: "hidden",
                    maxHeight: "92vh",
                },
            }}
        >
            <Box
                sx={{
                    background: `linear-gradient(150deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.secondary.main} 100%)`,
                    px: 2.5,
                    pt: 2.5,
                    pb: 2,
                    position: "relative",
                }}
            >
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        color: "white",
                        bgcolor: alpha("#fff", 0.15),
                        backdropFilter: "blur(4px)",
                        "&:hover": { bgcolor: alpha("#fff", 0.25) },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {companyData?.logo && (
                        <Box
                            sx={{
                                borderRadius: 3,
                                overflow: "hidden",
                                flexShrink: 0,
                                boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                                border: "2px solid rgba(255,255,255,0.25)",
                            }}
                        >
                            <ImageLoader
                                imagePath={companyData.logo}
                                width={64}
                                height={64}
                                alt={companyData?.name}
                                rounded={false}
                            />
                        </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 800, color: "white", lineHeight: 1.2 }}
                        >
                            {companyData?.name}
                        </Typography>
                        {companyData?.slogan && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: alpha("#fff", 0.7),
                                    fontStyle: "italic",
                                    display: "block",
                                    mt: 0.25,
                                }}
                            >
                                {companyData.slogan}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                mt: 0.75,
                            }}
                        >
                            {[1, 2, 3, 4, 5].map((s) => (
                                <StarIcon
                                    key={s}
                                    sx={{
                                        fontSize: 13,
                                        color:
                                            s <= Math.round(parseFloat(companyData?.averageRating || 0))
                                                ? "#fbbf24"
                                                : alpha("#fff", 0.25),
                                    }}
                                />
                            ))}
                            <Typography
                                variant="caption"
                                sx={{ color: "#fef3c7", fontWeight: 700, ml: 0.25 }}
                            >
                                {parseFloat(companyData?.averageRating || 0) > 0
                                    ? `${parseFloat(companyData.averageRating).toFixed(1)} (${companyData.totalRatings})`
                                    : "No ratings yet"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                    {companyData?.phone && (
                        <Chip
                            icon={<PhoneIcon />}
                            label={companyData.phone}
                            size="small"
                            sx={{
                                bgcolor: alpha("#fff", 0.12),
                                color: "white",
                                border: `1px solid ${alpha("#fff", 0.2)}`,
                                "& .MuiChip-icon": { color: "white", fontSize: 13 },
                                fontSize: "0.7rem",
                            }}
                        />
                    )}
                    {(companyData?.openTime || companyData?.closeTime) && (
                        <Chip
                            icon={<AccessTimeIcon />}
                            label={`${companyData.openTime || "--"} – ${companyData.closeTime || "--"}`}
                            size="small"
                            sx={{
                                bgcolor: alpha("#fff", 0.12),
                                color: "white",
                                border: `1px solid ${alpha("#fff", 0.2)}`,
                                "& .MuiChip-icon": { color: "#86efac", fontSize: 13 },
                                fontSize: "0.7rem",
                            }}
                        />
                    )}
                    {(companyData?.district || companyData?.country) && (
                        <Chip
                            icon={<LocationOnIcon />}
                            label={[companyData.district, companyData.country]
                                .filter(Boolean)
                                .join(", ")}
                            size="small"
                            sx={{
                                bgcolor: alpha("#fff", 0.12),
                                color: "white",
                                border: `1px solid ${alpha("#fff", 0.2)}`,
                                "& .MuiChip-icon": { color: "#fde68a", fontSize: 13 },
                                fontSize: "0.7rem",
                            }}
                        />
                    )}
                </Box>
            </Box>

            <DialogContent sx={{ p: 0, overflowY: "auto" }}>
                <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1.5,
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={700}>
                            Reviews {reviews.length > 0 && `(${reviews.length})`}
                        </Typography>
                    </Box>

                    {reviewsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : reviews.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 3, color: "text.disabled" }}>
                            <StarIcon sx={{ fontSize: 36, color: alpha("#f59e0b", 0.25), mb: 0.5 }} />
                            <Typography variant="body2" color="text.disabled">
                                No reviews yet — be the first!
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={1}>
                            {reviews.map((r) => (
                                <Paper
                                    key={r.id}
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 3,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                        bgcolor: alpha(theme.palette.background.default, 0.5),
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: "50%",
                                                    flexShrink: 0,
                                                    bgcolor: `hsl(${((r.customerName?.charCodeAt(0) || 65) * 5) % 360},55%,60%)`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: 700,
                                                    fontSize: "0.85rem",
                                                    color: "white",
                                                }}
                                            >
                                                {(r.customerName || "?")[0].toUpperCase()}
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={700}
                                                    sx={{ lineHeight: 1.1 }}
                                                >
                                                    {r.customerName || "Anonymous"}
                                                </Typography>
                                                <Typography variant="caption" color="text.disabled">
                                                    {new Date(r.createdAt).toLocaleDateString(undefined, {
                                                        month: "short",
                                                        day: "numeric",
                                                        year: "numeric",
                                                    })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: "flex" }}>
                                            {[1, 2, 3, 4, 5].map((s) => (
                                                <StarIcon
                                                    key={s}
                                                    sx={{
                                                        fontSize: 12,
                                                        color: s <= r.rating ? "#f59e0b" : alpha("#f59e0b", 0.18),
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                    {r.review && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                mt: 0.75,
                                                pl: 0.5,
                                                lineHeight: 1.55,
                                                fontStyle: "italic",
                                                fontSize: "0.8rem",
                                            }}
                                        >
                                            &ldquo;{r.review}&rdquo;
                                        </Typography>
                                    )}
                                </Paper>
                            ))}
                        </Stack>
                    )}
                </Box>

                <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <RateReviewIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                        <Typography variant="subtitle2" fontWeight={700}>
                            Write a Review
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 0.25,
                            mb: 1,
                        }}
                    >
                        {[1, 2, 3, 4, 5].map((star) => (
                            <IconButton
                                key={star}
                                disableRipple
                                onClick={() => setRatingForm((p) => ({ ...p, rating: star }))}
                                onMouseEnter={() =>
                                    setRatingForm((p) => ({ ...p, hoverRating: star }))
                                }
                                onMouseLeave={() =>
                                    setRatingForm((p) => ({ ...p, hoverRating: 0 }))
                                }
                                sx={{
                                    p: 0.4,
                                    transition: "transform 0.12s",
                                    "&:hover": {
                                        transform: "scale(1.25)",
                                        bgcolor: "transparent",
                                    },
                                }}
                            >
                                {(ratingForm.hoverRating || ratingForm.rating) >= star ? (
                                    <StarIcon sx={{ fontSize: 36, color: "#f59e0b" }} />
                                ) : (
                                    <StarBorderIcon sx={{ fontSize: 36, color: alpha("#f59e0b", 0.35) }} />
                                )}
                            </IconButton>
                        ))}
                    </Box>
                    {ratingForm.rating > 0 && (
                        <Box sx={{ textAlign: "center", mb: 1.5 }}>
                            <Chip
                                size="small"
                                label={
                                    ["Poor", "Fair", "Good", "Very Good", "Excellent"][
                                    ratingForm.rating - 1
                                    ]
                                }
                                sx={{
                                    fontWeight: 700,
                                    bgcolor: [
                                        alpha("#ef4444", 0.1),
                                        alpha("#f97316", 0.1),
                                        alpha("#eab308", 0.1),
                                        alpha("#22c55e", 0.1),
                                        alpha("#10b981", 0.1),
                                    ][ratingForm.rating - 1],
                                    color: [
                                        "#ef4444",
                                        "#f97316",
                                        "#eab308",
                                        "#22c55e",
                                        "#10b981",
                                    ][ratingForm.rating - 1],
                                    border: "1px solid currentColor",
                                }}
                            />
                        </Box>
                    )}

                    <Stack spacing={1.5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Your Name"
                            value={ratingForm.customerName}
                            onChange={(e) =>
                                setRatingForm((p) => ({ ...p, customerName: e.target.value }))
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon sx={{ fontSize: 16 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Phone (optional)"
                            value={ratingForm.customerPhone}
                            onChange={(e) =>
                                setRatingForm((p) => ({
                                    ...p,
                                    customerPhone: sanitizePhoneNumber(e.target.value).slice(0, 15),
                                }))
                            }
                            error={isRatingPhoneInvalid}
                            helperText={
                                isRatingPhoneInvalid
                                    ? "Enter a valid phone number (10-15 digits)"
                                    : " "
                            }
                            inputProps={{ inputMode: "numeric", maxLength: 15 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneIcon sx={{ fontSize: 16 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            label="Your Review"
                            placeholder="Tell us about your experience..."
                            value={ratingForm.review}
                            onChange={(e) =>
                                setRatingForm((p) => ({ ...p, review: e.target.value }))
                            }
                        />
                    </Stack>

                    <Button
                        fullWidth
                        variant="contained"
                        size="medium"
                        disabled={
                            ratingForm.rating === 0 ||
                            !ratingForm.customerName.trim() ||
                            isRatingPhoneInvalid ||
                            ratingSubmitting
                        }
                        startIcon={
                            ratingSubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />
                        }
                        onClick={onSubmitReview}
                        sx={{
                            mt: 2,
                            borderRadius: 3,
                            py: 1.25,
                            fontWeight: 700,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
                            "&:hover": {
                                boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.45)}`,
                            },
                        }}
                    >
                        {ratingSubmitting ? "Submitting..." : "Submit Review"}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CompanyInfoDialog;
